import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { DbProduct } from "@/hooks/useProduct";
import { Leaf } from "lucide-react";

interface CustomSection {
  title: string;
  content: string;
}

interface ProductAccordionProps {
  product: DbProduct;
}

export const ProductAccordion = ({ product }: ProductAccordionProps) => {
  // Prioritize regular glance_info over structured format for better compatibility
  const atAGlanceItems = product.glance_info && Array.isArray(product.glance_info) && product.glance_info.length > 0
    ? product.glance_info
    : product.glance_info_structured && Array.isArray(product.glance_info_structured) && product.glance_info_structured.length > 0
    ? product.glance_info_structured.map((info: any) => info.text || info)
    : [];

  const customSections: CustomSection[] = (product as any).custom_sections || [];
  const productBio = (product as any).bio;
  const productAdvantages = (product as any).product_advantages || [];
  const advantagesData = (product as any).advantages || {};
  // Handle both old array format and new object format
  const benefitsList = Array.isArray(advantagesData) ? [] : (advantagesData.benefitsList || []);
  const comparisonTable = Array.isArray(advantagesData) ? [] : (advantagesData.comparisonTable || []);
  const faqItems = (product as any).faq || [];

  // Create array of all possible accordion values to open by default
  // Care and FAQ are excluded so they start collapsed
  const defaultOpenSections = [
    "bio",
    "glance", 
    "light", 
    "self-watering",
    ...customSections.map((_, index) => `custom-${index}`),
    "benefits",
    "comparison"
  ];

  return (
    <div className="mt-6 md:mt-8 space-y-0">
      <Accordion type="multiple" defaultValue={defaultOpenSections} className="w-full">
        {/* At a Glance Section */}
        <AccordionItem value="glance" className="border-b border-border last:border-b-0">
          <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 text-left hover:no-underline hover:bg-muted/30 transition-colors">
            <span className="text-base md:text-lg font-semibold">{product.name} at a Glance</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
            {atAGlanceItems.length > 0 ? (
              <ul className="space-y-2 md:space-y-3">
                {atAGlanceItems.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-muted-foreground mr-2 md:mr-3 mt-1">•</span>
                    <span className="text-sm md:text-base text-foreground/80">{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm md:text-base text-muted-foreground italic">No details available</p>
            )}
          </AccordionContent>
        </AccordionItem>
        
        {/* Bio Section */}
        {productBio && (
          <AccordionItem value="bio" className="border-b border-border last:border-b-0">
            <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 text-left hover:no-underline hover:bg-muted/30 transition-colors">
              <span className="text-base md:text-lg font-semibold">{product.name} Bio</span>
            </AccordionTrigger>
            <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">{productBio}</p>
            </AccordionContent>
          </AccordionItem>
        )}
        
        {/* Light Instructions Section */}
        <AccordionItem value="light" className="border-b border-border last:border-b-0">
          <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 text-left hover:no-underline hover:bg-muted/30 transition-colors">
            <span className="text-base md:text-lg font-semibold">Light Instructions</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
            {(product.light_instruction_text || product.light_instruction_image_url) ? (
              <div className="space-y-4">
                {product.light_instruction_text && (
                  <p className="text-sm md:text-base text-foreground/80 leading-relaxed">{product.light_instruction_text}</p>
                )}
                {product.light_instruction_image_url && (
                  <img 
                    src={product.light_instruction_image_url} 
                    alt="Light instruction for plant care" 
                    className="w-full max-w-md rounded-lg"
                  />
                )}
              </div>
            ) : (
              <p className="text-sm md:text-base text-muted-foreground italic">No light instructions available</p>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Self-Watering Section */}
        <AccordionItem value="self-watering" className="border-b border-border last:border-b-0">
          <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 text-left hover:no-underline hover:bg-muted/30 transition-colors">
            <span className="text-base md:text-lg font-semibold">How Does Self-Watering Work?</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
            {(product as any).self_watering_info ? (
              <p className="text-sm md:text-base text-foreground/80 leading-relaxed whitespace-pre-wrap">{(product as any).self_watering_info}</p>
            ) : (
              <p className="text-sm md:text-base text-muted-foreground italic">No self-watering information available</p>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Paudhify Benefits Section */}
        <AccordionItem value="benefits" className="border-b border-border last:border-b-0">
          <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 text-left hover:no-underline hover:bg-muted/30 transition-colors">
            <span className="text-base md:text-lg font-semibold">Paudhify Benefits</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
            {benefitsList.length > 0 || comparisonTable.length > 0 ? (
              <div className="space-y-6">
                {/* Benefits List */}
                {benefitsList.length > 0 && (
                  <ul className="space-y-3">
                    {benefitsList.map((benefit: any, index: number) => {
                      // Split text by newlines in case it's stored as multi-line string
                      const lines = benefit.text?.split('\n').filter((line: string) => line.trim()) || [benefit.text];
                      return lines.map((line: string, lineIndex: number) => (
                        <li key={`${index}-${lineIndex}`} className="flex items-start gap-3">
                          <span className="text-primary mt-1 flex-shrink-0">•</span>
                          <span className="text-sm md:text-base text-foreground/80 leading-relaxed">{line.replace(/^\d+\.\s*/, '')}</span>
                        </li>
                      ));
                    })}
                  </ul>
                )}
                
                {/* Comparison Table */}
                {comparisonTable.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-border">
                          <th className="text-left py-3 px-2 text-sm md:text-base font-semibold">Advantages</th>
                          <th className="text-center py-3 px-2 text-sm md:text-base font-semibold text-primary">Paudhify</th>
                          <th className="text-center py-3 px-2 text-sm md:text-base font-semibold">Other</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonTable.map((row: any, index: number) => (
                          <tr key={index} className="border-b border-border">
                            <td className="py-3 px-2 text-sm md:text-base text-foreground/80">{row.advantage}</td>
                            <td className="py-3 px-2 text-sm md:text-base text-center font-medium">{row.brandValue}</td>
                            <td className="py-3 px-2 text-sm md:text-base text-center text-destructive">{row.otherValue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm md:text-base text-muted-foreground italic">No benefits information available</p>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Care and FAQ Section - Single Box */}
        {((product as any).care || faqItems.length > 0) && (
          <div className="mt-6 md:mt-8 bg-white rounded-xl p-4 md:p-6" style={{ 
            border: '1.5px solid rgba(55,120,90,0.12)',
            boxShadow: '0 6px 18px rgba(30,40,30,0.04)'
          }}>
            {/* Leaf Icon */}
            {(product as any).care && (
              <Leaf className="w-8 h-8 md:w-10 md:h-10 mb-4 md:mb-5" style={{ color: '#3c7858' }} />
            )}
            
            {/* Care Section - Not an Accordion */}
            {(product as any).care && (
              <div className="mb-8 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold leading-tight mb-4 md:mb-5" style={{ fontFamily: 'Georgia, serif', color: '#274732' }}>
                  {product.name} Care
                </h2>
                <p className="text-sm md:text-base leading-relaxed whitespace-pre-wrap" style={{ color: '#36583f', lineHeight: '1.6' }}>
                  {(product as any).care}
                </p>
              </div>
            )}

            {/* FAQ Section - Only FAQ Items are Accordions */}
            {faqItems.length > 0 && (
              <div>
                <h3 className="text-lg md:text-xl font-bold leading-tight mb-5 md:mb-6" style={{ fontFamily: 'Georgia, serif', color: '#274732' }}>
                  Frequently Asked Questions
                </h3>
                <Accordion type="single" collapsible className="w-full space-y-3 md:space-y-4">
                  {faqItems.map((item: any, index: number) => (
                    <AccordionItem 
                      key={index} 
                      value={`faq-${index}`} 
                      className="border-none bg-white rounded-xl overflow-hidden"
                      style={{ 
                        border: '1px solid rgba(55,120,90,0.12)',
                        boxShadow: '0 2px 8px rgba(30,40,30,0.02)'
                      }}
                    >
                      <AccordionTrigger className="px-5 md:px-6 py-4 md:py-5 text-left hover:no-underline hover:opacity-80 transition-opacity">
                        <span className="text-base md:text-lg font-medium leading-snug" style={{ color: '#213a2c' }}>
                          {item.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                        <p className="text-sm md:text-base leading-relaxed" style={{ color: '#36583f', lineHeight: '1.6' }}>
                          {item.answer}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        )}

        {/* Custom Sections */}
        {customSections.map((section, index) => (
          <AccordionItem key={index} value={`custom-${index}`} className="border-b border-border last:border-b-0">
            <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 text-left hover:no-underline hover:bg-muted/30 transition-colors">
              <span className="text-base md:text-lg font-semibold">{section.title}</span>
            </AccordionTrigger>
            <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
              <p className="text-sm md:text-base text-foreground/80 leading-relaxed whitespace-pre-wrap">{section.content}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
